import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  ShoppingCart as CartIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

/** Item in the section cart */
export interface CartItem {
  sectionId: string;
  sectionTitle: string;
  fromVersion: number;
  content: string;
}

interface SectionCartDrawerProps {
  open: boolean;
  onClose: () => void;
  items: CartItem[];
  onRemoveItem: (sectionId: string) => void;
  onClearCart: () => void;
  onApplyChanges: () => void;
  isApplying: boolean;
  currentVersion: number;
}

export function SectionCartDrawer({
  open,
  onClose,
  items,
  onRemoveItem,
  onClearCart,
  onApplyChanges,
  isApplying,
  currentVersion,
}: SectionCartDrawerProps) {
  const hasItems = items.length > 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose} slotProps={{ paper: {
        sx: { width: 360, maxWidth: '100vw' },
      } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CartIcon color="primary" />
            <Typography variant="h6">Section Cart</Typography>
            {hasItems && (
              <Chip
                label={items.length}
                size="small"
                color="primary"
                sx={{ height: 22 }}
              />
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {!hasItems ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                px: 3,
                textAlign: 'center',
              }}
            >
              <CartIcon sx={{ fontSize: 48, color: 'grey.300', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Your cart is empty
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click "Add to Cart" on any section from an older version to
                start building your changes.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary">
                  {items.length} section{items.length !== 1 ? 's' : ''} selected
                </Typography>
              </Box>

              <List disablePadding>
                {items.map((item, index) => (
                  <ListItem
                    key={item.sectionId}
                    divider={index < items.length - 1}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => onRemoveItem(item.sectionId)}
                        size="small"
                        disabled={isApplying}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                    sx={{ py: 1.5 }}
                  >
                    <ListItemText
                      primary={item.sectionTitle}
                      secondary={
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="span"
                        >
                          from version {item.fromVersion}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>

              <Box sx={{ px: 2, py: 1.5, bgcolor: 'info.50' }}>
                <Typography variant="body2" color="info.dark">
                  Other sections will remain at their current content (v
                  {currentVersion}).
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {/* Footer */}
        <Divider />
        <Box
          sx={{
            p: 2,
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClearCart}
            disabled={!hasItems || isApplying}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onApplyChanges}
            disabled={!hasItems || isApplying}
          >
            {isApplying ? 'Applying...' : 'Apply Changes'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

export default SectionCartDrawer;
